import React from 'react';
import { LinkOnClickEvent } from '@doveit/lego';
import ToolboxNavigationItem, { ToolboxNavigationItemProps } from './ToolboxNavigationItem';
import ToolboxNavigationMinimalItem from './ToolboxNavigationMinimalItem';
import * as styles from './ToolboxNavigation.style';

interface Item {
  id: string | number,
}

export interface ToolboxNavigationProps {
  activeItemId?: Item['id'],
  minimal?: boolean,
  onCommonClick?: () => void,
  onSelectedItemChange?: (item: Item) => void,
}

export interface ToolboxNavigationContextValue {
  activeItemId?: Item['id'],
  minimal?: boolean,
  onItemClick?: (item: Item & {
    customAction?: (event: LinkOnClickEvent) => void,
  }) => (event: LinkOnClickEvent) => void,
}

export const ToolboxContext = React.createContext<ToolboxNavigationContextValue>({
  minimal: false,
});

export interface ToolboxNavigationComposition {
  Item: typeof ToolboxNavigationItemWithContext,
}

export interface ToolboxNavigationItemWithContextProps extends ToolboxNavigationItemProps {
  id: Item['id'],
}

const ToolboxNavigationItemWithContext: React.FC<ToolboxNavigationItemWithContextProps> = ({
  id,
  onClick,
  ...rest
}) => (
  <ToolboxContext.Consumer>
    {({
      activeItemId,
      onItemClick,
      minimal,
    }) => {
      // eslint-disable-next-line react/no-unstable-nested-components
      const ItemComponent = () => {
        const ItemType = minimal
          ? ToolboxNavigationMinimalItem
          : ToolboxNavigationItem;

        return (
          <ItemType
            {...rest}
            onClick={onItemClick({ id, customAction: onClick })}
            active={activeItemId === id}
          />
        );
      };

      return (
        minimal
          ? <ItemComponent />
          : (
            <li css={styles.item}>
              <ItemComponent />
            </li>
          )
      );
    }}
  </ToolboxContext.Consumer>
);

const ToolboxNavigation: React.FC<ToolboxNavigationProps> & ToolboxNavigationComposition = ({
  activeItemId,
  minimal = false,
  onCommonClick,
  onSelectedItemChange,
  children,
}) => {
  const onItemClick: ToolboxNavigationContextValue['onItemClick'] = React.useCallback(({ id, customAction }) => (event) => {
    if (onSelectedItemChange) {
      onSelectedItemChange({ id });
    }

    if (customAction) {
      customAction(event);
      return;
    }

    if (onCommonClick) {
      onCommonClick();
    }
  }, [onSelectedItemChange, onCommonClick]);

  const contextValue: ToolboxNavigationContextValue = React.useMemo(() => ({
    activeItemId,
    minimal,
    onItemClick,
  }), [activeItemId, minimal, onItemClick]);

  return (
    <ToolboxContext.Provider value={contextValue}>
      <div
        css={styles.base}
        $minimal
      >
        <ul css={styles.content}>
          {children}
        </ul>
      </div>
    </ToolboxContext.Provider>
  );
};

ToolboxNavigation.Item = ToolboxNavigationItemWithContext;

export default ToolboxNavigation;
